import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";
import { sortRanking } from "@/lib/ranking";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { ArrowLeft, ArrowDown, Copy, Share2, Trophy, Users, Target, Calendar, Settings as SettingsIcon, Pencil, Image as ImageIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import Editor, { BtnBold, BtnItalic, BtnUnderline, BtnBulletList, BtnNumberedList, BtnLink, BtnUndo, BtnRedo, BtnClearFormatting, Separator as EditorSeparator, Toolbar } from "react-simple-wysiwyg";
import { phaseLabels, phaseOrder, formatKickoff } from "@/lib/utils-app";
import { MatchData } from "@/components/MatchCard";
import { TeamFlag } from "@/components/TeamFlag";
import { TeamLogosProvider } from "@/lib/team-logos";
import { PredictionForm } from "@/components/PredictionForm";
import { LeaderboardCard } from "@/components/LeaderboardCard";
import { PremiumBadge } from "@/components/PremiumBadge";
import { GroupPredictionsTab } from "@/components/GroupPredictionsTab";
import { SpecialBetsTab } from "@/components/SpecialBetsTab";
import { LeagueSpecialBetsSettings } from "@/components/LeagueSpecialBetsSettings";
import { AdSlot } from "@/components/ads/AdSlot";
import { InfoBanner } from "@/components/InfoBanner";
import { LeagueWelcomePopup } from "@/components/LeagueWelcomePopup";
import { useIsMobile } from "@/hooks/use-mobile";

import { toast } from "sonner";
import { sanitizeLeagueHtml, ALLOWED_TAGS } from "@/lib/sanitize-html";
import { Helmet } from "react-helmet-async";

interface League {
  id: string; name: string; invite_code: string; owner_id: string;
  exact_score_points: number; correct_outcome_points: number;
  tournament_id: string | null; lock_minutes_before_kickoff: number;
  logo_url: string | null; unique_results: boolean; description: string | null;
  is_premium: boolean; unique_exact_bonus: number;
  group_module_enabled: boolean;
  group_correct_position_points: number; group_correct_advancing_points: number;
  tips_visible_to_others: boolean;
  show_team_logos?: boolean;
  special_bets_enabled?: boolean;
  welcome_popup_enabled?: boolean;
  welcome_popup_html?: string | null;
  welcome_popup_updated_at?: string | null;
  banner_image_url?: string | null;
  banner_overlay_opacity?: number;
  correct_goals_points?: number;
}
interface Prediction { id: string; match_id: string; user_id: string; predicted_home_score: number; predicted_away_score: number; points_awarded: number; }
interface GroupPredictionRow { id: string; user_id: string; group_name: string; first_team: string; second_team: string; points_awarded: number; }
interface Member { user_id: string; role: string; nickname: string | null; }
interface StandingsRow { user_id: string; match_points: number; group_points: number; special_points?: number; exact_count: number; outcome_count: number; tips_count: number; }


const LeaguePage = () => {
  const { id } = useParams();
  const { user, isSuperAdmin } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") === "table" ? "table" : "overview";
  const [tab, setTab] = useState(initialTab);
  const [league, setLeague] = useState<League | null>(null);
  const [matches, setMatches] = useState<MatchData[]>([]);
  const [myPredictions, setMyPredictions] = useState<Prediction[]>([]);
  const [myGroupPredictions, setMyGroupPredictions] = useState<GroupPredictionRow[]>([]);
  const [standings, setStandings] = useState<StandingsRow[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [profiles, setProfiles] = useState<Record<string, { name: string }>>({});
  const [tournamentName, setTournamentName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [swipeDirection, setSwipeDirection] = useState<"left" | "right" | null>(null);
  const isMobile = useIsMobile();
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);


  const isAdmin = league && user && (league.owner_id === user.id || members.some(m => m.user_id === user.id && m.role === 'admin') || isSuperAdmin);
  const canDelete = !!(league && user && (league.owner_id === user.id || isSuperAdmin));

  const tabOrder = useMemo(() => {
    const order = ["overview", "matches"];
    if (league?.special_bets_enabled) order.push("special");
    order.push("table", "info");
    if (isAdmin) order.push("settings");
    return order;
  }, [league?.special_bets_enabled, isAdmin]);

  const changeTab = useCallback((nextTab: string, source: "click" | "swipe" = "click") => {
    const currentIdx = tabOrder.indexOf(tab);
    const nextIdx = tabOrder.indexOf(nextTab);
    if (currentIdx !== -1 && nextIdx !== -1 && nextIdx !== currentIdx) {
      setSwipeDirection(nextIdx > currentIdx ? "right" : "left");
    }
    setTab(nextTab);
    setParams({ tab: nextTab }, { replace: true });
    if (source === "swipe" && isMobile) {
      try {
        if (typeof navigator !== "undefined" && "vibrate" in navigator) {
          (navigator as Navigator & { vibrate: (pattern: number | number[]) => boolean }).vibrate(8);
        }
      } catch { /* ignore */ }
    }
  }, [tab, tabOrder, setParams, isMobile]);

  const isHorizontalScroller = (el: HTMLElement | null): boolean => {
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      const overflowX = style.overflowX;
      if (overflowX === "auto" || overflowX === "scroll") return true;
      el = el.parentElement;
    }
    return false;
  };

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].screenX;
    touchStartY.current = e.changedTouches[0].screenY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current == null || touchStartY.current == null) return;
    const endX = e.changedTouches[0].screenX;
    const endY = e.changedTouches[0].screenY;
    const deltaX = touchStartX.current - endX;
    const deltaY = touchStartY.current - endY;
    touchStartX.current = null;
    touchStartY.current = null;

    // Only handle horizontal swipes on mobile, ignore vertical scrolls and
    // swipes that start inside horizontally scrollable containers.
    if (!isMobile) return;
    if (Math.abs(deltaX) < Math.abs(deltaY)) return;
    if (Math.abs(deltaX) < 56) return;
    const target = e.target as HTMLElement;
    if (isHorizontalScroller(target)) return;

    const idx = tabOrder.indexOf(tab);
    if (idx === -1) return;
    if (deltaX > 0 && idx < tabOrder.length - 1) {
      changeTab(tabOrder[idx + 1], "swipe");
    } else if (deltaX < 0 && idx > 0) {
      changeTab(tabOrder[idx - 1], "swipe");
    }
  }, [isMobile, tab, tabOrder, changeTab]);

  const load = async () => {
    setLoading(true);
    const { data: lg } = await supabase.from("leagues").select("*").eq("id", id!).maybeSingle();
    if (!lg) { toast.error(t("league.notFound")); navigate("/"); return; }
    setLeague(lg as League);
    const matchesQ = supabase.from("matches").select("*").order("kickoff_at");
    const tournamentQ = lg.tournament_id
      ? supabase.from("tournaments").select("name,name_cs,name_en").eq("id", lg.tournament_id).maybeSingle()
      : Promise.resolve({ data: null });
    const [{ data: m }, { data: mem }, { data: tournament }] = await Promise.all([
      lg.tournament_id ? matchesQ.eq("tournament_id", lg.tournament_id) : matchesQ,
      supabase.from("league_members").select("*").eq("league_id", id!),
      tournamentQ,
    ]);
    setMatches((m as MatchData[]) ?? []);
    setMembers((mem as Member[]) ?? []);
    if (tournament) {
      const name = i18n.language.startsWith("cs")
        ? (tournament.name_cs || tournament.name)
        : (tournament.name_en || tournament.name);
      setTournamentName(name);
    } else {
      setTournamentName(null);
    }
    const userIds = (mem ?? []).map((x: any) => x.user_id);
    if (userIds.length) {
      const { data: profs } = await supabase.from("profiles_public").select("id,name").in("id", userIds);
      const map: Record<string, any> = {};
      (profs ?? []).forEach((p: any) => { map[p.id] = { name: p.name || "Hráč" }; });
      setProfiles(map);
    }
    const lgTyped = lg as League;
    const [pr, gp, st] = await Promise.all([
      supabase.from("predictions").select("*").eq("league_id", id!).eq("user_id", user!.id),
      lgTyped.is_premium && lgTyped.group_module_enabled
        ? supabase.from("group_predictions").select("*").eq("league_id", id!).eq("user_id", user!.id)
        : Promise.resolve({ data: [] as any[] } as any),
      supabase.rpc("get_league_standings", { _league_id: id! }),
    ]);
    setMyPredictions((pr.data as Prediction[]) ?? []);
    setMyGroupPredictions((gp.data as GroupPredictionRow[]) ?? []);
    setStandings((st.data as StandingsRow[]) ?? []);
    setLastUpdated(new Date());
    setLoading(false);
  };

  const refreshPredictions = async () => {
    if (!id || !league || !user) return;
    const [pr, gp, st] = await Promise.all([
      supabase.from("predictions").select("*").eq("league_id", id).eq("user_id", user.id),
      league.is_premium && league.group_module_enabled
        ? supabase.from("group_predictions").select("*").eq("league_id", id).eq("user_id", user.id)
        : Promise.resolve({ data: [] as any[] } as any),
      supabase.rpc("get_league_standings", { _league_id: id }),
    ]);
    setMyPredictions((pr.data as Prediction[]) ?? []);
    setMyGroupPredictions((gp.data as GroupPredictionRow[]) ?? []);
    setStandings((st.data as StandingsRow[]) ?? []);
    setLastUpdated(new Date());
  };


  useEffect(() => { if (user && id) load(); }, [user, id]);

  // Refresh tips when the tab/PWA returns to foreground or the window regains
  // focus. Without this, tips saved on another page (/matches, /predict) or on
  // another device stay invisible in this league's Overview until full reload,
  // and the user can hit a "duplicate" error trying to re-tip a match they
  // already tipped elsewhere.
  useEffect(() => {
    if (!user || !id || !league) return;
    const onVisible = () => { if (document.visibilityState === "visible") refreshPredictions(); };
    window.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refreshPredictions);
    return () => {
      window.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refreshPredictions);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id, league]);

  if (loading || !league) return <AppShell><p className="text-muted-foreground">{t("common.loading")}</p></AppShell>;

  return (
    <TeamLogosProvider enabled={league.show_team_logos !== false}>
    <AppShell title={league.name}>
      <Helmet>
        <title>{`${league.name} — Tipomatic`}</title>
        <meta name="description" content={`Tipovací liga ${league.name} — sleduj žebříček, zápasy a tipy členů.`} />
        <link rel="canonical" href={`https://tipomatic.app/league/${league.id}`} />
        <meta property="og:title" content={`${league.name} — Tipomatic`} />
        <meta property="og:url" content={`https://tipomatic.app/league/${league.id}`} />
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-4 w-4 mr-1" />{t("common.back")}
      </Link>

      {/* Hero card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-hero text-white shadow-elevated p-5 sm:p-6 mb-5">
        {league.banner_image_url ? (
          <>
            <img
              src={league.banner_image_url}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover"
              onError={(event) => { event.currentTarget.hidden = true; }}
            />
            <div
              className="absolute inset-0 bg-banner-overlay pointer-events-none"
              style={{ opacity: (league.banner_overlay_opacity ?? 68) / 100 }}
            />
          </>
        ) : (
          <>
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-white/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-primary/30 blur-3xl pointer-events-none" />
          </>
        )}
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex items-start gap-3">
            {league.logo_url && (
              <img src={league.logo_url} alt="" className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl object-cover ring-2 ring-white/30 shadow-lg shrink-0" />
            )}
            <div className="min-w-0">
            <div className="inline-flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ring-1 ring-white/20">
                <Trophy className="h-3.5 w-3.5" />{t("league.overview")}
              </div>
              {tournamentName && (
                <Badge className="bg-white/15 text-white border-white/20 hover:bg-white/25 backdrop-blur ring-1 ring-white/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
                  {tournamentName}
                </Badge>
              )}
              {league.is_premium && <PremiumBadge />}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold truncate mt-3">{league.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-white/70">{t("dashboard.invite")}</span>
              <span className="inline-flex items-center rounded-full bg-white/15 backdrop-blur ring-1 ring-white/25 px-3 py-1 font-mono text-sm font-semibold tracking-widest">
                {league.invite_code}
              </span>
            </div>
            </div>

          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/invite/${league.invite_code}`);
                toast.success(t("common.copied"));
              }}
              className="bg-white/15 hover:bg-white/25 text-white ring-1 ring-white/25 backdrop-blur shadow-none"
            >
              <Copy className="h-4 w-4 mr-2" />{t("common.copy")}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                const url = `${window.location.origin}/invite/${league.invite_code}`;
                const text = `${t("dashboard.invite")}: ${league.name}\n${url}`;
                if (navigator.share) {
                  navigator.share({ title: league.name, text, url }).catch(() => {});
                } else {
                  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
                }
              }}
              className="bg-white/15 hover:bg-white/25 text-white ring-1 ring-white/25 backdrop-blur shadow-none"
            >
              <Share2 className="h-4 w-4 mr-2" />{t("common.share")}
            </Button>
            {isAdmin && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setTab("settings"); setParams({ tab: "settings" }, { replace: true }); }}
                className="bg-white/10 hover:bg-white/20 text-white ring-1 ring-white/20 backdrop-blur shadow-none"
                aria-label={t("league.settings")}
              >
                <SettingsIcon className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t("league.settings")}</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      <LeagueWelcomePopup
        leagueId={league.id}
        leagueName={league.name}
        enabled={league.welcome_popup_enabled}
        html={league.welcome_popup_html}
        updatedAt={league.welcome_popup_updated_at}
      />

      <InfoBanner />

      <div className="league-tabs-wrapper" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <Tabs value={tab} onValueChange={(v) => changeTab(v, "click")}>
        <TabsList className="sticky top-16 z-30 flex w-full max-w-full justify-start overflow-x-auto no-scrollbar h-auto p-1 gap-1 bg-muted/95 backdrop-blur rounded-lg sm:justify-center sm:rounded-full shadow-card">
          {[
            { v: "overview", l: t("league.overview") },
            { v: "matches", l: t("league.matches") },
            ...(league.special_bets_enabled ? [{ v: "special", l: t("specialBets.title") }] : []),
            { v: "table", l: t("league.standings") },
            { v: "info", l: t("league.info") },
          ].map(({ v, l }) => (
            <TabsTrigger
              key={v}
              value={v}
              className="shrink-0 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-muted-foreground sm:px-4 data-[state=active]:bg-gradient-hero data-[state=active]:text-white data-[state=active]:shadow-gold transition-all"
            >
              {l}
            </TabsTrigger>
          ))}
        </TabsList>
        {isMobile && (
          <div className="flex justify-center items-center gap-2 mt-2 sm:hidden" aria-hidden="true">
            {tabOrder.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => changeTab(v, "click")}
                className={`swipe-dot ${tab === v ? "swipe-dot-active" : ""}`}
              />
            ))}
          </div>
        )}

        <AnimatedTabsContent value="overview" activeTab={tab} direction={swipeDirection}>
          <Overview league={league} matches={matches} predictions={myPredictions} groupPredictions={myGroupPredictions} members={members} standings={standings} userId={user!.id} onChange={refreshPredictions} />
          <AdSlot placement="league-inline" />
        </AnimatedTabsContent>
        <AnimatedTabsContent value="matches" activeTab={tab} direction={swipeDirection}>
          <MatchesWithGroups
            league={league}
            matches={matches}
            predictions={myPredictions}
            userId={user!.id}
            onChange={refreshPredictions}
          />
        </AnimatedTabsContent>
        {league.special_bets_enabled && (
          <AnimatedTabsContent value="special" activeTab={tab} direction={swipeDirection}>
            <SpecialBetsTab
              leagueId={league.id}
              tournamentId={league.tournament_id}
              userId={user!.id}
              matches={matches}
            />
          </AnimatedTabsContent>
        )}
        <AnimatedTabsContent value="table" activeTab={tab} direction={swipeDirection}>

          <Standings league={league} members={members} standings={standings} matches={matches} profiles={profiles} userId={user!.id} lastUpdated={lastUpdated} onChange={refreshPredictions} isAdmin={isAdmin} onReload={load} />
          <AdSlot placement="standings-bottom" />
        </AnimatedTabsContent>
        <AnimatedTabsContent value="info" activeTab={tab} direction={swipeDirection}>
          <InfoTab league={league} isAdmin={!!isAdmin} onChange={load} />
        </AnimatedTabsContent>
        {isAdmin && (
          <AnimatedTabsContent value="settings" activeTab={tab} direction={swipeDirection}>
            <Settings league={league} onChange={load} canDelete={canDelete} isSuperAdmin={isSuperAdmin} />
          </AnimatedTabsContent>
        )}


      </Tabs>
      </div>
    </AppShell>
    </TeamLogosProvider>
  );
};

const AnimatedTabsContent = ({ children, value, activeTab, direction, ...props }: any) => {
  const isActive = value === activeTab;
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (isActive) {
      const id = requestAnimationFrame(() => setMounted(true));
      return () => cancelAnimationFrame(id);
    }
    setMounted(false);
  }, [isActive]);
  if (!isActive) return null;
  const enterClass = direction === "right"
    ? "tab-content-from-right"
    : direction === "left"
      ? "tab-content-from-left"
      : "tab-content-from-right";
  return (
    <TabsContent
      value={value}
      className={`pt-5 tab-content-animated ${mounted ? "tab-content-active" : enterClass}`}
      {...props}
    >
      {children}
    </TabsContent>
  );
};

const StatCard = ({ icon: Icon, label, value }: any) => (
  <Card className="rounded-2xl shadow-card border-border overflow-hidden">
    <CardContent className="flex items-center gap-3 p-4">
      <div className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-hero text-primary-foreground shadow-gold">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold tabular-nums leading-tight">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const Overview = ({ league, matches, predictions, groupPredictions = [], members, standings = [], userId, onChange }: any) => {
  const { t } = useTranslation();
  const mine = predictions.filter((p: Prediction) => p.user_id === userId);
  const matchPoints = mine.reduce((s: number, p: Prediction) => s + p.points_awarded, 0);
  const groupPoints = league?.is_premium && league?.group_module_enabled
    ? (groupPredictions as GroupPredictionRow[])
        .filter((g) => g.user_id === userId)
        .reduce((s, g) => s + (g.points_awarded ?? 0), 0)
    : 0;
  const specialPoints = league?.special_bets_enabled
    ? Number((standings as StandingsRow[]).find((s) => s.user_id === userId)?.special_points ?? 0)
    : 0;
  const myPoints = matchPoints + groupPoints + specialPoints;
  const mineMap = useMemo(() => {
    const map: Record<string, Prediction> = {};
    mine.forEach((p: Prediction) => { map[p.match_id] = p; });
    return map;
  }, [mine]);
  const next = matches.find((m: MatchData) => new Date(m.kickoff_at).getTime() > Date.now());
  const sameDayMatches = useMemo(() => {
    if (!next) return [];
    const d = new Date(next.kickoff_at).toDateString();
    return matches
      .filter((m: MatchData) => new Date(m.kickoff_at).toDateString() === d)
      .sort((a: MatchData, b: MatchData) => new Date(a.kickoff_at).getTime() - new Date(b.kickoff_at).getTime());
  }, [matches, next]);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Trophy} label={t("league.myPoints")} value={myPoints} />
        <StatCard icon={Target} label={t("league.myTipsCount")} value={mine.length} />
        <StatCard icon={Users} label={t("league.players")} value={members.length} />
        <StatCard icon={Calendar} label={t("league.matchesCount")} value={matches.length} />
      </div>
      {next && (
        <Card className="overflow-hidden border-transparent shadow-elevated">
          <div className="bg-gradient-hero text-white px-5 py-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur ring-1 ring-white/20 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                <Calendar className="h-3 w-3" />{t("league.nextMatchDay")}
              </div>
              <p className="text-lg font-semibold mt-1.5 truncate">{formatKickoff(next.kickoff_at).split(",")[0]}</p>
            </div>
            <span className="hidden sm:inline-flex items-center rounded-full bg-white/15 backdrop-blur ring-1 ring-white/20 px-3 py-1 text-xs font-medium">
              {sameDayMatches.length} {t("league.matches").toLowerCase()}
            </span>
          </div>
          <CardContent className="space-y-2 p-4 bg-card">
            {sameDayMatches.map((m: MatchData) => (
              <PredictionForm
                key={m.id}
                match={m}
                existing={mineMap[m.id]}
                leagueId={league.id}
                userId={userId}
                onSaved={onChange}
                lockMinutesBefore={league.lock_minutes_before_kickoff}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const groupBy = (matches: MatchData[]) => {
  const g: Record<string, MatchData[]> = {};
  matches.forEach(m => { (g[m.phase] = g[m.phase] || []).push(m); });
  return g;
};

const MatchesWithGroups = ({ league, matches, predictions, userId, onChange }: any) => {
  const { t } = useTranslation();
  const [view, setView] = useState<"results" | "groups">("results");
  const showToggle = league.is_premium && league.group_module_enabled;
  return (
    <div className="space-y-4">
      {showToggle && (
        <div className="sticky top-0 z-20 -mx-4 px-4 py-2 bg-background/95 backdrop-blur-sm border-b border-border/50">
          <div className="grid grid-cols-2 gap-2 rounded-xl bg-muted p-1.5 border-2 border-primary/20 shadow-sm">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setView("results")}
                  className={`flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold uppercase tracking-wide transition-all ${view === "results" ? "bg-gradient-hero text-white shadow-gold scale-[1.02]" : "text-foreground/70 hover:text-foreground hover:bg-background/50"}`}
                >
                  <Trophy className="h-4 w-4" />
                  {t("league.resultsTab")}
                </button>
              </TooltipTrigger>
              <TooltipContent>{t("league.resultsTabTooltip")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setView("groups")}
                  className={`flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold uppercase tracking-wide transition-all ${view === "groups" ? "bg-gradient-hero text-white shadow-gold scale-[1.02]" : "text-foreground/70 hover:text-foreground hover:bg-background/50"}`}
                >
                  <Users className="h-4 w-4" />
                  {t("league.groupsTab")}
                </button>
              </TooltipTrigger>
              <TooltipContent>{t("league.groupsTabTooltip")}</TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}
      {view === "results" ? (
        <MatchesAndTips matches={matches} predictions={predictions} leagueId={league.id} userId={userId} onChange={onChange} lockMinutesBefore={league.lock_minutes_before_kickoff} />
      ) : (
        <GroupPredictionsTab
          leagueId={league.id}
          tournamentId={league.tournament_id}
          userId={userId}
          matches={matches}
          lockMinutesBefore={league.lock_minutes_before_kickoff}
          positionPoints={league.group_correct_position_points}
          advancingPoints={league.group_correct_advancing_points}
        />
      )}
    </div>
  );
};

const MatchesAndTips = ({ matches, predictions, leagueId, userId, onChange, lockMinutesBefore = 0 }: any) => {

  const { t, i18n } = useTranslation();
  const mineMap = useMemo(() => {
    const m: Record<string, Prediction> = {};
    predictions.filter((p: Prediction) => p.user_id === userId).forEach((p: Prediction) => { m[p.match_id] = p; });
    return m;
  }, [predictions, userId]);
  const byPhase = useMemo(() => groupBy(matches), [matches]);
  const availablePhases = useMemo(() => phaseOrder.filter((p) => byPhase[p]), [byPhase]);
  // Default phase filter based on today's date: if any match is played today,
  // pre-select the latest (most advanced) phase happening today. E.g. once the
  // round of 16 starts today, the "r16" chip is activated automatically.
  const defaultPhase = useMemo(() => {
    const today = new Date().toDateString();
    const todaysPhases = new Set(
      matches
        .filter((m: MatchData) => new Date(m.kickoff_at).toDateString() === today)
        .map((m: MatchData) => m.phase),
    );
    for (let i = phaseOrder.length - 1; i >= 0; i--) {
      if (todaysPhases.has(phaseOrder[i])) return phaseOrder[i];
    }
    return "all";
  }, [matches]);
  const [phaseFilter, setPhaseFilter] = useState<string>(defaultPhase);
  const didInitPhase = useRef(false);
  useEffect(() => {
    if (!didInitPhase.current && availablePhases.length) {
      didInitPhase.current = true;
      if (defaultPhase !== "all" && availablePhases.includes(defaultPhase)) {
        setPhaseFilter(defaultPhase);
      }
      return;
    }
    if (phaseFilter !== "all" && !availablePhases.includes(phaseFilter)) setPhaseFilter("all");
  }, [availablePhases, phaseFilter, defaultPhase]);
  const [now, setNow] = useState(Date.now());

  const nearest = useMemo(() => {
    const future = matches.filter((m: MatchData) => new Date(m.kickoff_at).getTime() > now);
    if (!future.length) return null;
    return future.reduce((a: MatchData, b: MatchData) =>
      new Date(a.kickoff_at).getTime() < new Date(b.kickoff_at).getTime() ? a : b
    );
  }, [matches, now]);

  const countdownFor = (m: MatchData): string | undefined => {
    if (nearest?.id !== m.id) return undefined;
    const ms = new Date(m.kickoff_at).getTime() - now;
    if (ms <= 0) return undefined;
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(ms / 3600000);
    const days = Math.floor(ms / 86400000);
    try {
      const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'always' });
      if (days > 0) return rtf.format(days, 'day');
      if (hours > 0) return rtf.format(hours, 'hour');
      return rtf.format(minutes, 'minute');
    } catch {
      if (days > 0) return `Za ${days}d`;
      if (hours > 0) return `Za ${hours}h ${minutes % 60}m`;
      return `Za ${minutes}m`;
    }
  };

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(id);
  }, []);

  const scrollToNearest = () => {
    if (!nearest) return;
    const el = document.getElementById(`match-${nearest.id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="space-y-6">
      {availablePhases.length > 1 && (
        <div className="-mx-4 px-4 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 min-w-max pb-1">
            <button
              type="button"
              onClick={() => setPhaseFilter("all")}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide border transition ${
                phaseFilter === "all"
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-background text-foreground/70 border-border hover:bg-muted"
              }`}
            >
              {t("common.all", "Vše")}
            </button>
            {availablePhases.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPhaseFilter(p)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide border transition ${
                  phaseFilter === p
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "bg-background text-foreground/70 border-border hover:bg-muted"
                }`}
              >
                {p === "group"
                  ? t("phases.groupShort", "Zákl. skupiny")
                  : p === "league_phase"
                    ? t("phases.leaguePhaseShort", "Ligová fáze")
                    : p === "playoff"
                      ? t("phases.playoffShort", "Play-off")
                      : t(`phases.${p}`, phaseLabels[p])}
              </button>
            ))}
          </div>
        </div>
      )}
      {nearest && (
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={scrollToNearest}>
            <ArrowDown className="h-4 w-4 mr-1" />
            {t("league.scrollToNearest")}
          </Button>
        </div>
      )}
      {availablePhases.filter((p) => phaseFilter === "all" || p === phaseFilter).map(p => (
        <div key={p}>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t(`phases.${p}`, phaseLabels[p])}</h3>
          <div className="space-y-2">
            {byPhase[p].map((m: MatchData) => (
              <div id={`match-${m.id}`} key={m.id}>
                <PredictionForm match={m} existing={mineMap[m.id]} leagueId={leagueId} userId={userId} onSaved={onChange} countdown={countdownFor(m)} lockMinutesBefore={lockMinutesBefore} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const Standings = ({ league, members, standings, matches, profiles, userId, lastUpdated, onChange, isAdmin, onReload }: any) => {
  const { t } = useTranslation();
  const rows = useMemo(() => {
    type RowOut = { user_id: string; name: string; points: number; exact: number; outcome: number; count: number };
    const byUser: Record<string, StandingsRow> = {};
    (standings as StandingsRow[]).forEach((s) => { byUser[s.user_id] = s; });
    const built: RowOut[] = members.map((mem: Member) => {
      const s = byUser[mem.user_id];
      const matchPts = Number(s?.match_points ?? 0);
      const groupPts = Number(s?.group_points ?? 0);
      const specialPts = Number(s?.special_points ?? 0);
      return {
        user_id: mem.user_id,
        name: (mem.nickname && mem.nickname.trim()) || profiles[mem.user_id]?.name || "Hráč",
        points: matchPts + groupPts + specialPts,
        exact: Number(s?.exact_count ?? 0),
        outcome: Number(s?.outcome_count ?? 0),
        count: Number(s?.tips_count ?? 0),
      };
    });
    return sortRanking(built);
  }, [members, standings, profiles]);


  const myMember = members.find((m: Member) => m.user_id === userId);

  const [editingNickname, setEditingNickname] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);

  const handleRemovePlayer = async (targetUserId: string) => {
    const { error } = await supabase
      .from("league_members")
      .delete()
      .eq("league_id", league.id)
      .eq("user_id", targetUserId);
    if (error) { toast.error(error.message); return; }
    toast.success(t("league.playerRemoved"));
    if (onReload) await onReload();
  };

  const selectedRow = rows.find((r: any) => r.user_id === selectedPlayer);

  return (
    <div className="space-y-3">
      {editingNickname && (
        <NicknameCard leagueId={league.id} userId={userId} initial={myMember?.nickname ?? ""} onSaved={() => { setEditingNickname(false); if (onReload) onReload(); else onChange(); }} />
      )}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{t("common.updated")}: {lastUpdated.toLocaleTimeString()}</span>
      </div>
      <LeaderboardCard
        rows={rows}
        currentUserId={userId}
        onEditNickname={() => setEditingNickname(!editingNickname)}
        editingNickname={editingNickname}
        isAdmin={!!isAdmin}
        ownerId={league.owner_id}
        onRemovePlayer={handleRemovePlayer}
        onPlayerClick={(uid) => setSelectedPlayer(uid)}
      />
      <PlayerTipsDialog
        open={!!selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
        playerName={selectedRow?.name ?? ""}
        userId={selectedPlayer}
        viewerUserId={userId}
        matches={matches}
        league={league}
      />
    </div>
  );
};


const NicknameCard = ({ leagueId, userId, initial, onSaved }: { leagueId: string; userId: string; initial: string; onSaved: () => void; }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(initial ?? ""); }, [initial]);

  const save = async () => {
    setSaving(true);
    const trimmed = value.trim();
    const { error } = await supabase
      .from("league_members")
      .update({ nickname: trimmed === "" ? null : trimmed })
      .eq("league_id", leagueId)
      .eq("user_id", userId);
    setSaving(false);
    if (error) toast.error(error.message);
    else { toast.success(t("league.saved")); onSaved(); }
  };

  return (
    <Card className="rounded-2xl shadow-card border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t("league.myNickname")}</CardTitle>
        <CardDescription className="text-xs">{t("league.nicknameHint")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t("league.nicknamePlaceholder")}
            maxLength={40}
            className="h-10 rounded-full"
          />
          <Button
            onClick={save}
            disabled={saving || value === (initial ?? "")}
            className="h-10 rounded-full bg-gradient-hero text-primary-foreground shadow-gold border-0 hover:opacity-95"
          >
            {t("common.save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Settings = ({ league, onChange, canDelete, isSuperAdmin }: any) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isPremium: userIsPremium } = useAuth();
  const [name, setName] = useState(league.name);
  const [exact, setExact] = useState(league.exact_score_points);
  const [outcome, setOutcome] = useState(league.correct_outcome_points);
  const [lockMin, setLockMin] = useState<number>(league.lock_minutes_before_kickoff ?? 0);
  const [uniqueResults, setUniqueResults] = useState<boolean>(!!league.unique_results);
  const [isPremiumLeague, setIsPremiumLeague] = useState<boolean>(!!league.is_premium);
  const [uniqueExactBonus, setUniqueExactBonus] = useState<number>(league.unique_exact_bonus ?? 1);
  const [groupModule, setGroupModule] = useState<boolean>(!!league.group_module_enabled);
  const [groupPosPts, setGroupPosPts] = useState<number>(league.group_correct_position_points ?? 2);
  const [groupAdvPts, setGroupAdvPts] = useState<number>(league.group_correct_advancing_points ?? 1);
  const [tipsVisible, setTipsVisible] = useState<boolean>(league.tips_visible_to_others !== false);
  const [showTeamLogos, setShowTeamLogos] = useState<boolean>(league.show_team_logos !== false);
  const [specialBets, setSpecialBets] = useState<boolean>(!!league.special_bets_enabled);
  const [correctGoalsPts, setCorrectGoalsPts] = useState<number>(league.correct_goals_points ?? 0);
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerOverlayOpacity, setBannerOverlayOpacity] = useState<number>(league.banner_overlay_opacity ?? 68);
  const fileRef = useRef<HTMLInputElement>(null);
  const bannerFileRef = useRef<HTMLInputElement>(null);

  const canTogglePremium = userIsPremium;

  const saveLeague = async () => {
    if (uniqueResults && !league.unique_results) {
      const { data: conflicts, error: checkErr } = await supabase.rpc("can_enable_unique_results", { _league_id: league.id });
      if (checkErr) { toast.error(checkErr.message); return; }
      if (conflicts && conflicts.length > 0) {
        toast.error(t("league.uniqueResultsConflict", { count: conflicts.length }));
        return;
      }
    }
    const { error } = await supabase.from("leagues").update({
      name, exact_score_points: exact, correct_outcome_points: outcome,
      lock_minutes_before_kickoff: Math.max(0, lockMin),
      unique_results: uniqueResults,
      is_premium: isPremiumLeague,
      unique_exact_bonus: Math.max(0, uniqueExactBonus),
      group_module_enabled: isPremiumLeague && groupModule,
      group_correct_position_points: Math.max(0, groupPosPts),
      group_correct_advancing_points: Math.max(0, groupAdvPts),
      tips_visible_to_others: tipsVisible,
      show_team_logos: showTeamLogos,
      special_bets_enabled: specialBets,
      correct_goals_points: Math.max(0, correctGoalsPts),
      banner_overlay_opacity: bannerOverlayOpacity,
    }).eq("id", league.id);
    if (error) toast.error(error.message); else { toast.success(t("league.saved")); onChange(); }
  };


  const uploadLogo = async (file: File) => {
    if (!file.type.startsWith("image/")) { toast.error(t("league.logoMustBeImage")); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error(t("league.logoTooLarge")); return; }
    setUploading(true);
    try {
      const mimeExtMap: Record<string, string> = {
        "image/png": "png",
        "image/jpeg": "jpg",
        "image/jpg": "jpg",
        "image/webp": "webp",
        "image/gif": "gif",
        "image/svg+xml": "svg",
      };
      const nameExt = file.name.includes(".") ? file.name.split(".").pop()!.toLowerCase() : "";
      const safeNameExt = /^[a-z0-9]{1,5}$/.test(nameExt) ? nameExt : "";
      const ext = mimeExtMap[file.type.toLowerCase()] || safeNameExt || "png";
      const path = `${league.id}/logo-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("league-logos")
        .upload(path, file, { upsert: false, contentType: file.type || "application/octet-stream", cacheControl: "3600" });
      if (upErr) {
        console.error("league logo upload failed", upErr);
        toast.error(upErr.message || "Upload failed");
        return;
      }
      const { data: { publicUrl } } = supabase.storage.from("league-logos").getPublicUrl(path);
      const { error: updErr } = await supabase.from("leagues").update({ logo_url: publicUrl }).eq("id", league.id);
      if (updErr) {
        console.error("league logo db update failed", updErr);
        toast.error(updErr.message);
        return;
      }
      toast.success(t("league.logoUpdated"));
      onChange();
    } catch (e: any) {
      console.error("league logo upload exception", e);
      toast.error(e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const testUpload = async () => {
    setUploading(true);
    try {
      // 1x1 transparent PNG
      const b64 = "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
      const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
      const file = new Blob([bytes], { type: "image/png" });
      const path = `${league.id}/rls-test-${Date.now()}.png`;
      const { error } = await supabase.storage
        .from("league-logos")
        .upload(path, file, { contentType: "image/png", upsert: false });
      if (error) {
        console.error("RLS test upload failed", { path, error });
        toast.error(`RLS test FAIL: ${error.message}`);
        return;
      }
      // cleanup
      await supabase.storage.from("league-logos").remove([path]);
      toast.success("RLS test OK — upload do storage funguje");
    } catch (e: any) {
      console.error("RLS test exception", e);
      toast.error(e?.message || "RLS test failed");
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    const { error } = await supabase.from("leagues").update({ logo_url: null }).eq("id", league.id);
    if (error) toast.error(error.message); else { toast.success(t("league.logoRemoved")); onChange(); }
  };

  const uploadBanner = async (file: File) => {
    if (!file.type.startsWith("image/")) { toast.error(t("league.bannerMustBeImage")); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error(t("league.bannerTooLarge")); return; }
    setUploadingBanner(true);
    try {
      const mimeExtMap: Record<string, string> = {
        "image/png": "png",
        "image/jpeg": "jpg",
        "image/jpg": "jpg",
        "image/webp": "webp",
        "image/gif": "gif",
      };
      const rawExt = file.name.includes(".") ? file.name.split(".").pop()?.toLowerCase() : "";
      const safeExt = rawExt && /^[a-z0-9]{1,5}$/.test(rawExt) ? rawExt : "";
      const ext = mimeExtMap[file.type.toLowerCase()] || safeExt || "jpg";
      const path = `${league.id}/banner-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("league-logos")
        .upload(path, file, { upsert: false, contentType: file.type, cacheControl: "3600" });
      if (uploadError) { toast.error(uploadError.message); return; }

      const { data: { publicUrl } } = supabase.storage.from("league-logos").getPublicUrl(path);
      const { error: updateError } = await supabase
        .from("leagues")
        .update({ banner_image_url: publicUrl })
        .eq("id", league.id);
      if (updateError) { toast.error(updateError.message); return; }
      toast.success(t("league.bannerUpdated"));
      onChange();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("league.bannerUploadFailed"));
    } finally {
      setUploadingBanner(false);
    }
  };

  const removeBanner = async () => {
    const { error } = await supabase.from("leagues").update({ banner_image_url: null }).eq("id", league.id);
    if (error) { toast.error(error.message); return; }
    toast.success(t("league.bannerRemoved"));
    onChange();
  };

  const deleteLeague = async () => {
    if (!confirm(t("league.confirmDeleteLeague"))) return;
    const { error } = await supabase.rpc("delete_league", { _league_id: league.id });
    if (error) { toast.error(error.message); return; }
    toast.success(t("league.leagueDeleted"));
    navigate("/");
  };

  return (
    <div className="space-y-4">
      <Card className="rounded-2xl shadow-card border-border overflow-hidden">
        <div className="bg-gradient-hero text-primary-foreground px-5 py-3">
          <CardTitle className="text-base text-primary-foreground">{t("league.leagueSettings")}</CardTitle>
        </div>
        <CardContent className="space-y-4 pt-5">
          <div className="space-y-2">
            <Label>{t("league.logo")}</Label>
            <div className="flex items-center gap-4">
              {league.logo_url ? (
                <img src={league.logo_url} alt="" className="h-16 w-16 rounded-xl object-cover ring-1 ring-border" />
              ) : (
                <div className="h-16 w-16 rounded-xl bg-muted flex items-center justify-center text-muted-foreground text-2xl font-bold">
                  {(league.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex flex-col gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }}
                />
                <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} className="rounded-full">
                  {uploading ? t("common.loading") : (league.logo_url ? t("league.changeLogo") : t("league.uploadLogo"))}
                </Button>
                {league.logo_url && (
                  <Button type="button" variant="ghost" size="sm" onClick={removeLogo} className="rounded-full text-destructive hover:text-destructive">
                    {t("league.removeLogo")}
                  </Button>
                )}
                {isSuperAdmin && (
                  <Button type="button" variant="secondary" size="sm" disabled={uploading} onClick={testUpload} className="rounded-full">
                    Test RLS upload
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t("league.logoHint")}</p>
          </div>
          <div className="space-y-2 border-t border-border pt-4">
            <Label>{t("league.bannerBackground")}</Label>
            <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-hero aspect-[3/1] min-h-28">
              {league.banner_image_url ? (
                <>
                  <img src={league.banner_image_url} alt={t("league.bannerPreviewAlt")} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-banner-overlay pointer-events-none" style={{ opacity: bannerOverlayOpacity / 100 }} />
                </>
              ) : (
                <div className="flex h-full min-h-28 items-center justify-center text-sm font-medium text-primary-foreground">
                  {t("league.bannerDefault")}
                </div>
              )}
            </div>
            <input
              ref={bannerFileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) uploadBanner(file);
                event.target.value = "";
              }}
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" disabled={uploadingBanner} onClick={() => bannerFileRef.current?.click()} className="rounded-full">
                <ImageIcon className="mr-2 h-4 w-4" />
                {uploadingBanner ? t("common.loading") : (league.banner_image_url ? t("league.changeBanner") : t("league.uploadBanner"))}
              </Button>
              {league.banner_image_url && (
                <Button type="button" variant="ghost" size="sm" onClick={removeBanner} disabled={uploadingBanner} className="rounded-full text-destructive hover:text-destructive">
                  {t("league.useDefaultBanner")}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{t("league.bannerHint")}</p>
            {league.banner_image_url && (
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="banner-overlay-opacity">{t("league.bannerDarkness")}</Label>
                  <span className="text-sm tabular-nums text-muted-foreground">{bannerOverlayOpacity} %</span>
                </div>
                <Slider
                  id="banner-overlay-opacity"
                  min={0}
                  max={100}
                  step={5}
                  value={[bannerOverlayOpacity]}
                  onValueChange={(value) => setBannerOverlayOpacity(value[0] ?? 68)}
                  aria-label={t("league.bannerDarkness")}
                />
                <p className="text-xs text-muted-foreground">{t("league.bannerDarknessHint")}</p>
              </div>
            )}
          </div>
          <div className="space-y-2"><Label>{t("league.name")}</Label><Input value={name} onChange={e => setName(e.target.value)} className="h-11 rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>{t("league.exactPts")}</Label><Input type="number" value={exact} onChange={e => setExact(parseInt(e.target.value) || 0)} className="h-11 rounded-xl" /></div>
            <div className="space-y-2"><Label>{t("league.outcomePts")}</Label><Input type="number" value={outcome} onChange={e => setOutcome(parseInt(e.target.value) || 0)} className="h-11 rounded-xl" /></div>
          </div>
          <div className="space-y-2">
            <Label>{t("league.lockMinutesBefore")}</Label>
            <Input type="number" min={0} value={lockMin} onChange={e => setLockMin(Math.max(0, parseInt(e.target.value) || 0))} className="h-11 rounded-xl" />
            <p className="text-xs text-muted-foreground">{t("league.lockMinutesBeforeHint")}</p>
          </div>
          <div className="space-y-2">
            <Label>{t("league.correctGoalsPts")}</Label>
            <Input type="number" min={0} value={correctGoalsPts}
              onChange={e => setCorrectGoalsPts(Math.max(0, parseInt(e.target.value) || 0))} className="h-11 rounded-xl" />
            <p className="text-xs text-muted-foreground">{t("league.correctGoalsPtsHint")}</p>
          </div>
          <div className="space-y-2">
            <Label>{t("league.infoUniqueBonus")}</Label>
            <Input type="number" min={0} value={uniqueExactBonus}
              onChange={(e) => setUniqueExactBonus(Math.max(0, parseInt(e.target.value) || 0))}
              className="h-11 rounded-xl" />
            <p className="text-xs text-muted-foreground">{t("league.uniqueBonusHint")}</p>
          </div>
          <div className="flex items-start justify-between gap-3 rounded-xl border border-border p-3">
            <div className="space-y-1">
              <Label htmlFor="unique-results-switch" className="cursor-pointer">{t("league.uniqueResults")}</Label>
              <p className="text-xs text-muted-foreground">{t("league.uniqueResultsHint")}</p>
            </div>
            <Switch id="unique-results-switch" checked={uniqueResults} onCheckedChange={setUniqueResults} />
          </div>
          <div className="flex items-start justify-between gap-3 rounded-xl border border-border p-3">
            <div className="space-y-1">
              <Label htmlFor="tips-visible-switch" className="cursor-pointer">{t("league.tipsVisible")}</Label>
              <p className="text-xs text-muted-foreground">{t("league.tipsVisibleHint")}</p>
            </div>
            <Switch id="tips-visible-switch" checked={tipsVisible} onCheckedChange={setTipsVisible} />
          </div>
          <div className="flex items-start justify-between gap-3 rounded-xl border border-border p-3">
            <div className="space-y-1">
              <Label htmlFor="team-logos-switch" className="cursor-pointer">{t("league.showTeamLogos")}</Label>
              <p className="text-xs text-muted-foreground">{t("league.showTeamLogosHint")}</p>
            </div>
            <Switch id="team-logos-switch" checked={showTeamLogos} onCheckedChange={setShowTeamLogos} />
          </div>

          <div className="space-y-3 rounded-xl border border-border p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <Label htmlFor="special-bets-switch" className="cursor-pointer">{t("specialBets.enable")}</Label>
                <p className="text-xs text-muted-foreground">{t("specialBets.enableHint")}</p>
              </div>
              <Switch id="special-bets-switch" checked={specialBets} onCheckedChange={setSpecialBets} />
            </div>
            {specialBets && (
              <LeagueSpecialBetsSettings leagueId={league.id} tournamentId={league.tournament_id} />
            )}
          </div>


          {/* Premium settings */}
          <div className="rounded-2xl border border-amber-300/40 bg-gradient-to-br from-amber-50/60 to-orange-50/40 dark:from-amber-950/20 dark:to-orange-950/10 p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <Label htmlFor="premium-league-switch" className="cursor-pointer flex items-center gap-2">
                  <PremiumBadge label="Premium liga" />
                </Label>
                <p className="text-xs text-muted-foreground">
                  {canTogglePremium
                    ? "Aktivuj rozšířené bodování a tipování postupujících ze skupin."
                    : "Premium ligu může vytvořit pouze premium uživatel. Požádej superadmina o premium status."}
                </p>
              </div>
              <Switch
                id="premium-league-switch"
                checked={isPremiumLeague}
                onCheckedChange={setIsPremiumLeague}
                disabled={!canTogglePremium && !league.is_premium}
              />
            </div>

            {isPremiumLeague && (
              <>
                <div className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card/50 p-3">
                  <div className="space-y-1">
                    <Label htmlFor="group-module-switch" className="cursor-pointer">Modul: tipování postupujících ze skupin</Label>
                    <p className="text-xs text-muted-foreground">Hráči tipují 1. a 2. místo ve skupině.</p>
                  </div>
                  <Switch id="group-module-switch" checked={groupModule} onCheckedChange={setGroupModule} />
                </div>

                {groupModule && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Body za správnou pozici</Label>
                      <Input type="number" min={0} value={groupPosPts}
                        onChange={(e) => setGroupPosPts(Math.max(0, parseInt(e.target.value) || 0))}
                        className="h-10 rounded-xl" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Body za postupujícího mimo pozici</Label>
                      <Input type="number" min={0} value={groupAdvPts}
                        onChange={(e) => setGroupAdvPts(Math.max(0, parseInt(e.target.value) || 0))}
                        className="h-10 rounded-xl" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>


          <Button onClick={saveLeague} className="w-full h-11 rounded-full bg-gradient-hero text-primary-foreground shadow-gold border-0 hover:opacity-95">{t("common.save")}</Button>
        </CardContent>
      </Card>
      <Card className="rounded-2xl shadow-card border-border bg-muted/40">
        <CardHeader>
          <CardTitle className="text-base">{t("league.resultsManagedByAdmin")}</CardTitle>
          <CardDescription>{t("league.resultsManagedByAdminDesc")}</CardDescription>
        </CardHeader>
      </Card>
      {canDelete && (
        <Card className="rounded-2xl border-destructive/40 bg-destructive/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-destructive">{t("league.dangerZone")}</CardTitle>
            <CardDescription>{t("league.deleteLeagueHint")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" onClick={deleteLeague} className="rounded-full">
              {t("league.deleteLeague")}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const PlayerTipsDialog = ({
  open, onClose, playerName, userId, viewerUserId, matches, league,
}: {
  open: boolean;
  onClose: () => void;
  playerName: string;
  userId: string | null;
  viewerUserId?: string | null;
  matches: MatchData[];
  league?: any;
}) => {
  const { t, i18n } = useTranslation();
  const hideFuture = !!(league && league.tips_visible_to_others === false && viewerUserId && userId && viewerUserId !== userId);

  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [groupPredictions, setGroupPredictions] = useState<GroupPredictionRow[]>([]);
  const [loadingTips, setLoadingTips] = useState(false);
  const showGroups = !!(league?.is_premium && league?.group_module_enabled);

  useEffect(() => {
    if (!open || !userId || !league?.id) return;
    let cancelled = false;
    setLoadingTips(true);
    (async () => {
      const [pr, gp] = await Promise.all([
        supabase.rpc("get_player_league_predictions", { _league_id: league.id, _user_id: userId }),
        showGroups
          ? supabase.from("group_predictions").select("*").eq("league_id", league.id).eq("user_id", userId)
          : Promise.resolve({ data: [] as any[] } as any),
      ]);
      if (cancelled) return;
      setPredictions((pr.data as Prediction[]) ?? []);
      setGroupPredictions((gp.data as GroupPredictionRow[]) ?? []);
      setLoadingTips(false);
    })();
    return () => { cancelled = true; };
  }, [open, userId, league?.id, showGroups]);

  const rows = useMemo(() => {
    if (!userId) return [];
    const matchMap: Record<string, MatchData> = {};
    matches.forEach((m) => { matchMap[m.id] = m; });
    return predictions
      .filter((p) => p.user_id === userId)
      .map((p) => ({ p, m: matchMap[p.match_id] }))
      .filter((x) => x.m)
      .filter((x) => !hideFuture || new Date(x.m.kickoff_at).getTime() <= Date.now())
      .sort((a, b) => new Date(a.m.kickoff_at).getTime() - new Date(b.m.kickoff_at).getTime());
  }, [userId, predictions, matches, hideFuture]);

  const totalPoints = rows.reduce((s, r) => s + r.p.points_awarded, 0);
  const fmt = (d: string) => {
    try { return new Date(d).toLocaleString(i18n.language, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return new Date(d).toLocaleString(); }
  };

  const allMyGroups = useMemo(() => {
    if (!userId || !showGroups) return [];
    return (groupPredictions ?? [])
      .filter((g) => g.user_id === userId)
      .sort((a, b) => a.group_name.localeCompare(b.group_name));
  }, [userId, groupPredictions, showGroups]);

  const [groupResults, setGroupResults] = useState<Record<string, { first_team: string; second_team: string }>>({});
  useEffect(() => {
    if (!open || !showGroups || !league?.tournament_id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("group_results").select("group_name, first_team, second_team").eq("tournament_id", league.tournament_id);
      if (cancelled) return;
      const map: Record<string, { first_team: string; second_team: string }> = {};
      (data ?? []).forEach((r: any) => { map[r.group_name] = { first_team: r.first_team, second_team: r.second_team }; });
      setGroupResults(map);
    })();
    return () => { cancelled = true; };
  }, [open, showGroups, league?.tournament_id]);
  const groupFirstKickoff = useMemo(() => {
    const map: Record<string, number> = {};
    matches.forEach((m) => {
      if (!m.group_name) return;
      const t = new Date(m.kickoff_at).getTime();
      if (map[m.group_name] === undefined || t < map[m.group_name]) map[m.group_name] = t;
    });
    return map;
  }, [matches]);
  const myGroups = useMemo(
    () => (hideFuture
      ? allMyGroups.filter((g) => {
          const first = groupFirstKickoff[g.group_name];
          return first !== undefined && first <= Date.now();
        })
      : allMyGroups),
    [allMyGroups, hideFuture, groupFirstKickoff]
  );
  const groupPoints = myGroups.reduce((s, g) => s + (g.points_awarded ?? 0), 0);
  const [dialogTab, setDialogTab] = useState<"results" | "groups">("results");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("league.playerTipsTitle", { name: playerName })}</DialogTitle>
        </DialogHeader>
        {loadingTips ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t("common.loading")}</p>
        ) : rows.length === 0 && myGroups.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {hideFuture ? t("league.noTipsYet") : t("league.noTipsEmpty")}
          </p>
        ) : (
          <Tabs value={dialogTab} onValueChange={(v) => setDialogTab(v as "results" | "groups")} className="mt-2">
            {showGroups && myGroups.length > 0 && (
              <TabsList className="w-full">
                <TabsTrigger value="results" className="flex-1">{t("league.resultsTab")}</TabsTrigger>
                <TabsTrigger value="groups" className="flex-1">{t("league.groupsTab")}</TabsTrigger>
              </TabsList>
            )}
            <TabsContent value="results" className="mt-4 space-y-4">
              {rows.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  {hideFuture ? t("league.noTipsYet") : t("league.noTipsEmpty")}
                </p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                    <span>{rows.length} {t("league.tipsShort").toLowerCase()}</span>
                    <span>
                      {t("league.pointsShort")}:{" "}
                      <b className="text-primary text-sm tabular-nums">{totalPoints}</b>
                    </span>
                  </div>
                  {rows.map(({ p, m }) => {
                    const hasResult = m.home_score !== null && m.away_score !== null;
                    const isExact = hasResult && p.predicted_home_score === m.home_score && p.predicted_away_score === m.away_score;
                    return (
                      <div
                        key={p.id}
                        className="rounded-xl border border-border bg-card p-3 flex items-center gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            {fmt(m.kickoff_at)}
                          </div>
                          <div className="text-sm font-medium truncate">
                            {m.home_team} – {m.away_team}
                          </div>
                          <div className="mt-1 flex items-center gap-3 text-xs">
                            <span className="text-muted-foreground">
                              {t("league.tipColumn")}:{" "}
                              <b className="text-foreground tabular-nums">
                                {p.predicted_home_score}:{p.predicted_away_score}
                              </b>
                            </span>
                            <span className="text-muted-foreground">
                              {t("league.resultColumn")}:{" "}
                              <b className="text-foreground tabular-nums">
                                {hasResult ? `${m.home_score}:${m.away_score}` : t("league.notPlayedYet")}
                              </b>
                            </span>
                          </div>
                        </div>
                        <div
                          className={`shrink-0 grid place-items-center min-w-[44px] h-11 rounded-lg text-sm font-bold tabular-nums ${
                            !hasResult
                              ? "bg-muted text-muted-foreground"
                              : p.points_awarded > 0
                              ? isExact
                                ? "bg-gradient-gold text-primary-foreground shadow-gold"
                                : "bg-gradient-pitch text-primary-foreground"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {hasResult ? `+${p.points_awarded}` : "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
            {showGroups && myGroups.length > 0 && (
              <TabsContent value="groups" className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                  <span className="font-semibold uppercase tracking-wider">{t("groups.tab")}</span>
                  <span>
                    {t("league.pointsShort")}:{" "}
                    <b className="text-primary text-sm tabular-nums">{groupPoints}</b>
                  </span>
                </div>
                {myGroups.map((g) => {
                  const res = groupResults[g.group_name];
                  const hasResult = !!res;
                  return (
                    <div key={g.id} className="rounded-xl border border-border bg-card p-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          {t("groups.group")} {g.group_name}
                        </div>
                        <div className="mt-1 flex flex-col gap-0.5 text-xs">
                          <span className="text-muted-foreground">
                            {t("league.tipColumn")}:{" "}
                            <b className="text-foreground">1. {g.first_team} · 2. {g.second_team}</b>
                          </span>
                          <span className="text-muted-foreground">
                            {t("league.resultColumn")}:{" "}
                            <b className="text-foreground">
                              {hasResult ? `1. ${res.first_team} · 2. ${res.second_team}` : t("league.notPlayedYet")}
                            </b>
                          </span>
                        </div>
                      </div>
                      <div
                        className={`shrink-0 grid place-items-center min-w-[44px] h-11 rounded-lg text-sm font-bold tabular-nums ${
                          !hasResult
                            ? "bg-muted text-muted-foreground"
                            : (g.points_awarded ?? 0) > 0
                            ? "bg-gradient-pitch text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {hasResult ? `+${g.points_awarded ?? 0}` : "—"}
                      </div>
                    </div>
                  );
                })}
              </TabsContent>
            )}
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

const RichEditor = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const { t } = useTranslation();
  const [htmlMode, setHtmlMode] = useState(false);
  const [imgOpen, setImgOpen] = useState(false);
  const [imgUrl, setImgUrl] = useState("");
  const [imgAlt, setImgAlt] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);

  const rememberSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const ce = wrapRef.current?.querySelector<HTMLElement>(".rsw-ce");
      const range = sel.getRangeAt(0);
      if (ce && ce.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    }
  };

  const restoreSelection = () => {
    const ce = wrapRef.current?.querySelector<HTMLElement>(".rsw-ce");
    if (!ce) return;
    ce.focus();
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    if (savedRangeRef.current) {
      sel.addRange(savedRangeRef.current);
    } else {
      const range = document.createRange();
      range.selectNodeContents(ce);
      range.collapse(false);
      sel.addRange(range);
    }
  };

  const insertImage = (url: string, alt: string) => {
    if (!url) return;
    restoreSelection();
    const safeAlt = alt.replace(/"/g, "&quot;");
    const ok = document.execCommand("insertHTML", false, `<img src="${url}" alt="${safeAlt}" />`);
    if (!ok) {
      // Fallback: append to end
      const ce = wrapRef.current?.querySelector<HTMLElement>(".rsw-ce");
      if (ce) {
        ce.innerHTML = ce.innerHTML + `<img src="${url}" alt="${safeAlt}" />`;
        onChange(ce.innerHTML);
      }
    } else {
      const ce = wrapRef.current?.querySelector<HTMLElement>(".rsw-ce");
      if (ce) onChange(ce.innerHTML);
    }
  };

  const onFilePicked = (file: File | null) => {
    if (!file) return;
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t("league.imgInvalidType"));
      return;
    }
    const maxSize = 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(t("league.imgTooLarge"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImgUrl(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const submitImage = () => {
    const url = imgUrl.trim();
    if (!url) return;
    const isHttp = /^https?:\/\//i.test(url);
    const isData = /^data:image\/(png|jpe?g|gif|webp);base64,/i.test(url);
    if (!isHttp && !isData) {
      toast.error(t("league.imgInvalidUrl"));
      return;
    }
    if (isHttp) {
      try {
        const pathname = new URL(url).pathname.toLowerCase();
        if (!/\.(png|jpe?g|gif|webp)$/i.test(pathname)) {
          toast.error(t("league.imgInvalidType"));
          return;
        }
      } catch {
        toast.error(t("league.imgInvalidUrl"));
        return;
      }
    }
    insertImage(url, imgAlt.trim());
    setImgOpen(false);
    setImgUrl("");
    setImgAlt("");
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2">
        <Label htmlFor="html-source-switch" className="cursor-pointer text-xs font-medium">
          {t("league.htmlSource")}
        </Label>
        <Switch id="html-source-switch" checked={htmlMode} onCheckedChange={setHtmlMode} />
      </div>
      {htmlMode ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={12}
          className="rounded-xl font-mono text-xs"
          placeholder="<h2>…</h2>"
        />
      ) : (
        <div
          ref={wrapRef}
          className="rounded-xl border border-input bg-background overflow-hidden [&_.rsw-editor]:border-0 [&_.rsw-ce]:min-h-[200px] [&_.rsw-ce]:p-3 [&_.rsw-ce]:text-sm [&_.rsw-ce]:outline-none [&_.rsw-ce_img]:max-w-full [&_.rsw-ce_img]:h-auto [&_.rsw-toolbar]:bg-muted/50 [&_.rsw-toolbar]:border-b [&_.rsw-toolbar]:border-border [&_.rsw-btn]:text-foreground [&_.rsw-btn:hover]:bg-accent"
        >
          <Editor value={value} onChange={(e: any) => onChange(e.target.value)}>
            <Toolbar>
              <BtnUndo />
              <BtnRedo />
              <EditorSeparator />
              <BtnBold />
              <BtnItalic />
              <BtnUnderline />
              <EditorSeparator />
              <BtnBulletList />
              <BtnNumberedList />
              <EditorSeparator />
              <BtnLink />
              <button
                type="button"
                title={t("league.insertImage")}
                aria-label={t("league.insertImage")}
                className="rsw-btn"
                onMouseDown={(e) => { e.preventDefault(); rememberSelection(); }}
                onClick={(e) => { e.preventDefault(); setImgOpen(true); }}
              >
                <ImageIcon className="h-4 w-4" />
              </button>
              <BtnClearFormatting />
            </Toolbar>
          </Editor>
        </div>
      )}

      <Dialog open={imgOpen} onOpenChange={setImgOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("league.insertImage")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="img-url" className="text-xs">{t("league.imgUrlLabel")}</Label>
              <Input
                id="img-url"
                value={imgUrl}
                onChange={(e) => setImgUrl(e.target.value)}
                placeholder="https://… nebo data:image/png;base64,…"
                className="rounded-xl"
              />
              <p className="text-[11px] text-muted-foreground">{t("league.imgUrlHint")}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="img-file" className="text-xs">{t("league.imgFromFile")}</Label>
              <input
                id="img-file"
                type="file"
                accept="image/*"
                onChange={(e) => onFilePicked(e.target.files?.[0] ?? null)}
                className="block w-full text-xs file:mr-3 file:rounded-full file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-xs file:font-medium hover:file:bg-accent"
              />
              <p className="text-[11px] text-muted-foreground">{t("league.imgFileHint")}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="img-alt" className="text-xs">{t("league.imgAlt")}</Label>
              <Input
                id="img-alt"
                value={imgAlt}
                onChange={(e) => setImgAlt(e.target.value)}
                placeholder={t("league.imgAltPlaceholder")}
                maxLength={120}
                className="rounded-xl"
              />
            </div>
            {imgUrl && (
              <div className="rounded-xl border border-border p-2 bg-muted/30">
                <p className="text-[11px] text-muted-foreground mb-1">{t("league.imgPreview")}</p>
                <img src={imgUrl} alt="" className="max-h-40 mx-auto object-contain" />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setImgOpen(false)} className="rounded-full">{t("common.cancel")}</Button>
              <Button onClick={submitImage} disabled={!imgUrl.trim()} className="rounded-full bg-gradient-hero text-primary-foreground shadow-gold border-0 hover:opacity-95">
                {t("league.insert")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const WelcomePopupSettings = ({ league, onChange }: { league: League; onChange: () => void | Promise<void> }) => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState<boolean>(!!league.welcome_popup_enabled);
  const [html, setHtml] = useState<string>(league.welcome_popup_html ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(!!league.welcome_popup_enabled);
    setHtml(league.welcome_popup_html ?? "");
  }, [league.welcome_popup_enabled, league.welcome_popup_html]);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from("leagues").update({
      welcome_popup_enabled: enabled,
      welcome_popup_html: html.trim() === "" ? null : sanitizeLeagueHtml(html).clean,
    }).eq("id", league.id);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(t("league.saved"));
    await onChange();
  };

  return (
    <Card className="rounded-2xl shadow-card border-border overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t("league.popupEnable")}</CardTitle>
        <CardDescription className="text-xs">{t("league.popupEnableHint")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start justify-between gap-3 rounded-xl border border-border p-3">
          <Label htmlFor="welcome-popup-switch" className="cursor-pointer">{t("league.popupEnable")}</Label>
          <Switch id="welcome-popup-switch" checked={enabled} onCheckedChange={setEnabled} />
        </div>
        {enabled && (
          <div className="space-y-2">
            <Label>{t("league.popupText")}</Label>
            <RichEditor value={html} onChange={setHtml} />
            <p className="text-xs text-muted-foreground">{t("league.descriptionHint")}</p>
          </div>
        )}
        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={saving} className="rounded-full bg-gradient-hero text-primary-foreground shadow-gold border-0 hover:opacity-95">
            {t("common.save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const InfoTab = ({ league, isAdmin, onChange }: { league: League; isAdmin: boolean; onChange: () => void | Promise<void> }) => {
  const { t } = useTranslation();
  const description = league.description;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<string>(description ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(description ?? ""); }, [description]);

  const html = useMemo(
    () => (description ? sanitizeLeagueHtml(description).clean : ""),
    [description]
  );

  const save = async () => {
    setSaving(true);
    let sanitized: string | null = null;
    if (value.trim() !== "") {
      const result = sanitizeLeagueHtml(value);
      sanitized = result.clean;
      const blocked = [
        ...result.removedTags.map((x) => `<${x}>`),
        ...result.removedAttrs.map((x) => `${x}=…`),
      ];
      if (blocked.length > 0) {
        toast.warning(t("league.descriptionBlocked", { items: blocked.join(", ") }), {
          description: t("league.descriptionAllowedHint", { tags: ALLOWED_TAGS.join(", ") }),
          duration: 8000,
        });
      }
    }
    const { error } = await supabase.from("leagues").update({ description: sanitized }).eq("id", league.id);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(t("league.saved"));
    setEditing(false);
    await onChange();
  };

  const InfoItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="rounded-xl border border-border bg-muted/30 p-3">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold">{value}</p>
    </div>
  );
  const onOff = (b: boolean) => (b ? t("league.infoOn") : t("league.infoOff"));
  const pts = (n: number) => `${n} ${t("common.points")}`;

  const rulesCard = (
    <Card className="rounded-2xl shadow-card border-border overflow-hidden">
      <div className="bg-gradient-hero text-white px-5 py-3">
        <CardTitle className="text-base text-white">{t("league.infoRules")}</CardTitle>
      </div>
      <CardContent className="pt-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-border bg-muted/30 p-3">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("league.exactPts")}</p>
            <p className="text-lg font-bold tabular-nums">{league.exact_score_points} {t("common.points")}</p>
          </div>
          <div className="rounded-xl border border-border bg-muted/30 p-3">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("league.outcomePts")}</p>
            <p className="text-lg font-bold tabular-nums">{league.correct_outcome_points} {t("common.points")}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InfoItem label={t("league.correctGoalsPts")} value={league.correct_goals_points ? pts(league.correct_goals_points) : t("league.infoOff")} />
          <InfoItem
            label={t("league.lockMinutesBefore")}
            value={league.lock_minutes_before_kickoff === 0
              ? t("league.infoLockAtKickoff")
              : t("league.infoLockMinutes", { minutes: league.lock_minutes_before_kickoff })}
          />
          <InfoItem label={t("league.uniqueResults")} value={onOff(!!league.unique_results)} />
          <InfoItem label={t("league.tipsVisible")} value={onOff(!!league.tips_visible_to_others)} />
          <InfoItem label={t("league.showTeamLogos")} value={onOff(!!league.show_team_logos)} />
          <InfoItem label={t("specialBets.title")} value={onOff(!!league.special_bets_enabled)} />
          <InfoItem label={t("league.infoUniqueBonus")} value={pts(league.unique_exact_bonus ?? 0)} />
          {league.is_premium && (
            <InfoItem label={t("league.infoGroupModule")} value={onOff(!!league.group_module_enabled)} />
          )}
          {league.is_premium && league.group_module_enabled && (
            <>
              <InfoItem label={t("league.infoGroupPosPts")} value={pts(league.group_correct_position_points ?? 0)} />
              <InfoItem label={t("league.infoGroupAdvPts")} value={pts(league.group_correct_advancing_points ?? 0)} />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );


  if (editing) {
    return (
      <div className="space-y-4">
        <Card className="rounded-2xl shadow-card border-border">
          <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base">{t("league.description")}</CardTitle>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setValue(description ?? ""); setEditing(false); }} disabled={saving} className="rounded-full">
                {t("common.cancel")}
              </Button>
              <Button size="sm" onClick={save} disabled={saving} className="rounded-full bg-gradient-hero text-primary-foreground shadow-gold border-0 hover:opacity-95">
                {t("common.save")}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-2 space-y-2">
            <RichEditor value={value} onChange={setValue} />
            <p className="text-xs text-muted-foreground">{t("league.descriptionHint")}</p>
          </CardContent>
        </Card>
        {rulesCard}
        {isAdmin && <WelcomePopupSettings league={league} onChange={onChange} />}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="rounded-2xl shadow-card border-border">
        {isAdmin && (
          <div className="flex justify-end px-4 pt-4">
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="rounded-full" aria-label={t("common.edit")}>
              <Pencil className="h-4 w-4 mr-1.5" />{t("common.edit")}
            </Button>
          </div>
        )}
        <CardContent className={isAdmin ? "pt-2" : "pt-5"}>
          {!description || description.trim() === "" ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t("league.noDescription")}</p>
          ) : (
            <div
              className="prose prose-sm dark:prose-invert max-w-none break-words"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
        </CardContent>
      </Card>
      {rulesCard}
      {isAdmin && <WelcomePopupSettings league={league} onChange={onChange} />}
    </div>
  );
};

export default LeaguePage;
